/**
 * NOT CURRENTLY USED.
 *
 * The all-programs attendance list is no longer reachable: the Attendance nav entry goes
 * to the portal root, which lands on today's default program, and switching programs
 * happens inline via GroupSwitcher on the roster itself. Its route stays registered in
 * StaffApp so old links resolve.
 *
 * It also predates the "today only" rule — it lists programs regardless of whether they
 * meet today. Left as-is on purpose; do not treat it as a current example.
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Users, ChevronRight, CalendarDays } from 'lucide-react';
import { supabase } from '../supabase';
import { StaffLayout } from '../StaffLayout';
import { getTodaySessionDate } from '../../utils/schedule';
import { formatDateDisplay } from '../../utils/date';
import { getScheduleDescription } from '../../types/program';
import { NON_TEACHING_TYPES_FILTER, recentSessionDates } from '../programSessions';
import { REAL_SESSION_KEY } from '../../types/attendance';

interface StaffUser {
  id: string;
  email: string;
  name: string | null;
  program_ids: string[] | null;
}

interface ProgramRow {
  id: string;
  name: string;
  type: string;
  schedule_cron: string;
  start_date: string;
  end_date: string;
  /** Per-program no-class dates; `recentSessionDates` skips them. */
  excluded_dates: string[] | null;
}

interface DayStats {
  date: string;
  present: number;
}

interface OverviewItem {
  program: ProgramRow;
  enrolled: number;
  days: DayStats[];
}

interface AttendanceRow {
  program_id: string;
  session_date: string;
  status: string;
}

interface Props {
  staffUser: StaffUser;
}

/** '2026-06-11' → '6/11'. DATE column — no TZ conversion needed. */
function shortDate(dateStr: string): string {
  const [, mm, dd] = dateStr.split('-');
  return `${parseInt(mm)}/${parseInt(dd)}`;
}

/**
 * How many past sessions the per-program card summarizes. A camp week fits entirely; a
 * ten-month after-school program would otherwise render ~200 chips and blow out the card.
 */
const RECENT_SESSION_LIMIT = 10;

// Exported so StaffRoster can invalidate this cache on unmount.
export const ATTENDANCE_OVERVIEW_KEY = 'staff-attendance-overview';

export function StaffAttendance({ staffUser }: Props) {
  const navigate = useNavigate();
  const scopedIds = staffUser.program_ids;
  const today = getTodaySessionDate();

  // Single combined query: programs + enrolled counts (via staff_camp_roster, which
  // includes Full Summer Pass students) + per-day attendance — all in one shot so
  // there is no dependency chain and no TanStack v5 "disabled query isLoading=false"
  // race that showed 0 enrolled before the dependent stats query fired.
  const { data: overview = [], isLoading } = useQuery<OverviewItem[]>({
    queryKey: [ATTENDANCE_OVERVIEW_KEY, scopedIds, today],
    queryFn: async () => {
      // 1. Fetch the teacher's active programs
      let q = supabase
        .from('programs')
        .select('id, name, type, schedule_cron, start_date, end_date, excluded_dates, brand_key')
        .eq('active', true)
        .not('type', 'in', NON_TEACHING_TYPES_FILTER);
      if (scopedIds) q = q.in('id', scopedIds);
      const { data: progsData, error: progsErr } = await q.order('start_date');
      if (progsErr) throw progsErr;
      const progs = (progsData ?? []) as ProgramRow[];
      if (progs.length === 0) return [];

      const ids = progs.map(p => p.id);

      // The last few days each program actually met, derived from its schedule. Querying
      // these explicitly keeps the attendance fetch proportional to what the cards render
      // rather than to the program's whole history.
      const recentByProgram = new Map(
        progs.map(p => [p.id, recentSessionDates(p, today, RECENT_SESSION_LIMIT)]),
      );
      const sessionDates = Array.from(new Set([...recentByProgram.values()].flat()));

      // 2. Enrolled counts via staff_camp_roster (same RPC as Schedule → Roster tab):
      //    includes Full Summer Pass students with date-overlap dedup.
      //    Attendance records for those sessions — in parallel.
      const [rosterCounts, attendanceRes] = await Promise.all([
        Promise.all(
          ids.map(id =>
            supabase
              .rpc('staff_camp_roster', { p_program_id: id })
              .then(r => {
                if (r.error) throw r.error;
                return { id, count: (r.data ?? []).length };
              }),
          ),
        ),
        sessionDates.length > 0
          ? supabase
              .from('attendance_records')
              .select('program_id, session_date, status')
              .in('program_id', ids)
              .in('session_date', sessionDates)
              // This page is unreachable (see the note at the top of the file), but the
              // filter goes in anyway: its per-program present counts are keyed on
              // program_id, which virtual rows share, so whoever revives this screen would
              // otherwise find after-school attendance silently tripled.
              .eq('virtual_class_key', REAL_SESSION_KEY)
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (attendanceRes.error) throw attendanceRes.error;

      const enrolledMap = Object.fromEntries(rosterCounts.map(r => [r.id, r.count]));

      // 3. Per-day present counts grouped by (program_id, session_date)
      const dayMap: Record<string, Record<string, number>> = {};
      for (const row of (attendanceRes.data ?? []) as AttendanceRow[]) {
        if (row.status !== 'checked_in' && row.status !== 'picked_up') continue;
        if (!dayMap[row.program_id]) dayMap[row.program_id] = {};
        dayMap[row.program_id][row.session_date] =
          (dayMap[row.program_id][row.session_date] ?? 0) + 1;
      }

      // Show every recent session, including ones with nobody marked present, so a
      // forgotten day reads as a 0 rather than silently vanishing from the strip.
      return progs.map(p => ({
        program: p,
        enrolled: enrolledMap[p.id] ?? 0,
        days: (recentByProgram.get(p.id) ?? []).map(date => ({
          date,
          present: dayMap[p.id]?.[date] ?? 0,
        })),
      }));
    },
  });

  return (
    <StaffLayout staffUser={staffUser}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Today: {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      ) : overview.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <CheckCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No programs assigned yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {overview.map(({ program: p, enrolled, days }) => (
            <button
              key={p.id}
              onClick={() => navigate(`/staff-app/attendance/${p.id}`)}
              className="w-full bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-blue-300 hover:shadow-sm transition-all group"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 truncate">{p.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{getScheduleDescription(p.schedule_cron)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatDateDisplay(p.start_date)} – {formatDateDisplay(p.end_date)}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-blue-400 flex-shrink-0 mt-0.5" />
              </div>

              <div className="flex items-center gap-4 mt-3">
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <Users className="h-4 w-4 text-gray-400" />
                  <span>{enrolled} enrolled</span>
                </div>
                {today < p.start_date ? (
                  <div className="flex items-center gap-1.5 text-sm text-gray-400">
                    <CalendarDays className="h-4 w-4" />
                    <span>Starts {shortDate(p.start_date)}</span>
                  </div>
                ) : days.length > 0 ? (
                  <div className="flex items-center gap-1.5">
                    <CheckCircle className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="flex flex-wrap gap-2">
                      {days.map(d => (
                        <span key={d.date} className="inline-flex items-baseline gap-0.5">
                          <span className={`text-sm font-semibold ${d.present > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                            {d.present}
                          </span>
                          <span className="text-xs text-gray-400">{shortDate(d.date)}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-sm text-gray-400">
                    <CheckCircle className="h-4 w-4" />
                    <span>No attendance yet</span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </StaffLayout>
  );
}
